import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, User, CreditCard, DollarSign, Receipt, UserPlus, Loader2, CheckCircle, BadgeCheck, Printer, Copy } from 'lucide-react';
import PaymentModal from '../components/PaymentModal';
import unifiedPrintingService from '../utils/unifiedPrintingService';
import stripeTerminal from '@/utils/stripeTerminal';
import { useCart } from '@/context/CartContext';

export default function InvoicePayment() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { clear } = useCart();
  
  // Check if rewards system is enabled
  const [rewardsEnabled, setRewardsEnabled] = useState(false);
  const [rewardsSettings, setRewardsSettings] = useState({
    enabled: false,
    checkboxFieldId: '',
    checkboxOptionValue: 'Yes'
  });

  // Fetch rewards settings from API
  useEffect(() => {
    const fetchRewardsSettings = async () => {
      try {
        const { data } = await api.get('/settings/rewards');
        console.log('[InvoicePayment] Fetched rewards settings:', data);
        setRewardsSettings(data);
        setRewardsEnabled(data.enabled || false);
      } catch (error) {
        console.error('[InvoicePayment] Failed to fetch rewards settings:', error);
        // Fallback to localStorage
        const s = localStorage.getItem('rewards_settings');
        if (s) {
          const settings = JSON.parse(s);
          setRewardsSettings(settings);
          setRewardsEnabled(settings.enabled || false);
        }
      }
    };
    fetchRewardsSettings();
    
    // Load CC fee settings from admin settings API (with localStorage fallback)
    const fetchCcFeeSettings = async () => {
      try {
        const { data } = await api.get('/settings/admin');
        if (data.cc_fee_enabled !== undefined) {
          setCcFeeEnabled(data.cc_fee_enabled);
        }
        if (data.cc_fee_rate !== undefined) {
          setCcFeeRate(data.cc_fee_rate);
        }
        if (data.receipt_settings) {
          if (data.receipt_settings.allow_complete_without_printing !== undefined) {
            setAllowCompleteWithoutPrinting(data.receipt_settings.allow_complete_without_printing);
          }
          
          // Sync all admin receipt settings to localStorage for the print function to use
          const existingStr = localStorage.getItem('receipt_settings');
          const existingSettings = existingStr ? JSON.parse(existingStr) : {};
          
          const newSettings = {
            ...existingSettings,
            showLogo: data.receipt_settings.show_logo !== undefined ? data.receipt_settings.show_logo : existingSettings.showLogo,
            showPhone: data.receipt_settings.show_phone !== undefined ? data.receipt_settings.show_phone : existingSettings.showPhone,
            showEmail: data.receipt_settings.show_email !== undefined ? data.receipt_settings.show_email : existingSettings.showEmail,
            showBarcode: data.receipt_settings.show_barcode !== undefined ? data.receipt_settings.show_barcode : existingSettings.showBarcode,
            thankYouMessage: data.receipt_settings.thank_you_message !== undefined ? data.receipt_settings.thank_you_message : existingSettings.thankYouMessage,
            allowCompleteWithoutPrinting: data.receipt_settings.allow_complete_without_printing !== undefined ? data.receipt_settings.allow_complete_without_printing : existingSettings.allowCompleteWithoutPrinting,
            useBackgroundWorker: data.receipt_settings.use_background_worker !== undefined ? data.receipt_settings.use_background_worker : existingSettings.useBackgroundWorker,
            // CRITICAL: Sync printer name from backend to prevent cross-account printer leaks
            selectedPrinter: data.last_selected_printer || null
          };
          localStorage.setItem('receipt_settings', JSON.stringify(newSettings));
        }
        console.log('[InvoicePayment] CC fee settings:', { enabled: data.cc_fee_enabled, rate: data.cc_fee_rate });
      } catch (error) {
        console.error('[InvoicePayment] Failed to fetch Admin settings from API:', error);
        // Fallback to localStorage
        const cached = localStorage.getItem('cc_fee_settings');
        if (cached) {
          const settings = JSON.parse(cached);
          setCcFeeEnabled(settings.enabled || false);
          setCcFeeRate(settings.rate || 0.03);
        }
        const receiptCached = localStorage.getItem('receipt_settings');
        if (receiptCached) {
          const receiptSettings = JSON.parse(receiptCached);
          setAllowCompleteWithoutPrinting(receiptSettings.allowCompleteWithoutPrinting || false);
        }
      }
    };
    fetchCcFeeSettings();
  }, []);
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [processing, setProcessing] = useState(false);
  const [drawerOpenedAndPrinted, setDrawerOpenedAndPrinted] = useState(false);
  
  // Stripe Terminal Reader info
  const connectedReader = useMemo(() => {
    const saved = localStorage.getItem('stripe_terminal_reader');
    return saved ? JSON.parse(saved) : null;
  }, []);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [terminalProcessing, setTerminalProcessing] = useState(false);
  const [terminalStatus, setTerminalStatus] = useState('');
  const [showTerminalDialog, setShowTerminalDialog] = useState(false);
  const [terminalConnectionFailed, setTerminalConnectionFailed] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [createdInvoiceId, setCreatedInvoiceId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [cashTendered, setCashTendered] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address1: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US'
  });
  const [enableRewardsForNewCustomer, setEnableRewardsForNewCustomer] = useState(true);
  const [activateRewardsDialogOpen, setActivateRewardsDialogOpen] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
  const [paymentMethodSelected, setPaymentMethodSelected] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [qzTrayAvailable, setQzTrayAvailable] = useState(false);
  const [allowCompleteWithoutPrinting, setAllowCompleteWithoutPrinting] = useState(false);
  const [printPromptOpen, setPrintPromptOpen] = useState(false);
  const [pendingReceiptData, setPendingReceiptData] = useState(null);
  
  // Credit Card Fee Settings
  const [ccFeeEnabled, setCcFeeEnabled] = useState(false);
  const [ccFeeRate, setCcFeeRate] = useState(0.03); // Decimal (0.03 = 3%)
  const [rewardsForm, setRewardsForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address1: '',
    city: '',
    state: '',
    country: '',
    postalCode: ''
  });

  // Define fetchInvoice BEFORE the useEffect that calls it
  const fetchInvoice = useCallback(async () => {
    try {
      const { data } = await api.get(`/live/invoices/${invoiceId}`, { params: { live: 1 } });
      console.log('Fetched invoice data:', data);
      console.log('Contact details:', data.contactDetails);
      
      setInvoice(data);
      
      // If invoice has contact details, set as selected customer
      if (data.contactDetails) {
        const customer = {
          id: data.contactDetails.id,
          name: data.contactDetails.name,
          email: data.contactDetails.email,
          phone: data.contactDetails.phoneNo
        };
        console.log('Setting selected customer:', customer);
        setSelectedCustomer(customer);
      } else {
        console.log('No contact details found in invoice');
      }
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoice details',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [invoiceId, toast]);

  useEffect(() => {
    // Check if printing service is available and try to connect
    const checkPrinting = async () => {
      const available = unifiedPrintingService.isAvailable();
      const envInfo = unifiedPrintingService.getEnvironmentInfo();
      console.log('[Printing] Service available:', available, envInfo);
      
      if (available) {
        try {
          await unifiedPrintingService.initialize();
          setQzTrayAvailable(true);
          console.log('[Printing] Successfully initialized and connected');
        } catch (error) {
          console.warn('[Printing] Available but connection failed:', error.message);
          setQzTrayAvailable(false);
        }
      } else {
        console.warn('[Printing] Service not available');
        setQzTrayAvailable(false);
      }
    };
    
    checkPrinting();
    
    // Check if we have cart data (new invoice) or existing invoice
    // Use location.state directly but don't depend on it to avoid re-render loops
    if (location.state?.cartData) {
      // New invoice from cart - don't fetch, just set as pending
      setInvoice(location.state.cartData);
      
      // Load previously selected customer from cart data
      if (location.state.cartData.selectedCustomer) {
        console.log('Loading customer from cart data:', location.state.cartData.selectedCustomer);
        setSelectedCustomer(location.state.cartData.selectedCustomer);
      }
      
      setLoading(false);
    } else if (location.state?.invoice) {
      // Existing invoice passed from elsewhere
      setInvoice(location.state.invoice);
      setLoading(false);
    } else if (invoiceId && invoiceId !== 'new') {
      // Fetch existing invoice by ID
      fetchInvoice();
    } else {
      setLoading(false);
    }
  }, [invoiceId, fetchInvoice]); // Only depend on invoiceId and fetchInvoice

  // Debounced search function
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    
    try {
      const { data } = await api.post('/live/contacts/search', 
        { query },
        { headers: { 'X-Use-Live': 'true' } }
      );
      setSearchResults(data.contacts || []);
    } catch (error) {
      console.error('Customer search failed:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchCustomers(customerSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);

  // Simple inline calculations - no memoization to avoid dependency issues
  const baseInvoiceTotal = invoice?.total || invoice?.amountDue || 0;
  
  // Calculate CC fee amount (only for card payments when enabled)
  const ccFeeAmount = (ccFeeEnabled && paymentMethod === 'card') 
    ? +(baseInvoiceTotal * ccFeeRate).toFixed(2) 
    : 0;
  
  // Final invoice total includes CC fee when applicable
  const invoiceTotal = +(baseInvoiceTotal + ccFeeAmount).toFixed(2);
  
  const cashAmount = parseFloat(cashTendered) || 0;
  const changeOwed = cashAmount - invoiceTotal;

  // Check if contact is already a rewards member
  const isRewardsMember = (contact) => {
    if (!rewardsSettings.checkboxFieldId) {
      console.log('[Rewards Check] No checkboxFieldId configured');
      return false;
    }

    const customFields = contact.customFields || contact.customField || [];
    console.log('[Rewards Check] Contact:', contact.id || contact.name, 'Custom fields count:', customFields.length);
    
    if (Array.isArray(customFields)) {
      // Try to find by ID first
      let rewardsField = customFields.find(
        field => field.id === rewardsSettings.checkboxFieldId
      );
      
      // If not found by ID, try by key
      if (!rewardsField) {
        rewardsField = customFields.find(
          field => field.key === rewardsSettings.checkboxFieldId
        );
      }
      
      console.log('[Rewards Check] Found rewards field:', rewardsField ? 'YES' : 'NO');
      
      if (rewardsField) {
        const value = rewardsField.field_value || rewardsField.value;
        const checkValue = rewardsSettings.checkboxOptionValue || 'Yes';
        
        console.log('[Rewards Check] Field value:', value, 'Expected:', checkValue);
        
        if (Array.isArray(value)) {
          // Case-insensitive comparison for array values - check all common variants
          const result = value.some(v => 
            String(v).toLowerCase() === String(checkValue).toLowerCase() ||
            String(v).toLowerCase() === 'yes'
          );
          console.log('[Rewards Check v2] Array check result:', result, '| Values:', value.map(v => String(v).toLowerCase()), '| Expected:', String(checkValue).toLowerCase());
          return result;
        }
        
        // Case-insensitive comparison for string values
        const result = String(value).toLowerCase() === String(checkValue).toLowerCase() ||
                       String(value).toLowerCase() === 'yes';
        console.log('[Rewards Check] String check result:', result);
        return result;
      } else {
        console.log('[Rewards Check] Rewards field not found in contact custom fields');
      }
    } else {
      console.log('[Rewards Check] customFields is not an array:', typeof customFields);
    }
    
    return false;
  };

  // Open rewards activation dialog
  // Quick action helpers
  const copyInvoiceNumber = () => {
    const invoiceNumber = invoice.invoiceNumber || invoiceId;
    
    // Fallback method for iframes where Clipboard API is blocked
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(invoiceNumber).then(() => {
          toast({
            title: 'Copied!',
            description: `Order #${invoiceNumber} copied to clipboard`
          });
        }).catch(() => {
          // Fall back to legacy method
          copyToClipboardFallback(invoiceNumber);
        });
      } else {
        // Use fallback immediately if Clipboard API not available
        copyToClipboardFallback(invoiceNumber);
      }
    } catch (error) {
      console.error('Copy failed:', error);
      copyToClipboardFallback(invoiceNumber);
    }
  };

  const copyToClipboardFallback = (text) => {
    // Create temporary textarea
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    
    try {
      textarea.select();
      textarea.setSelectionRange(0, 99999); // For mobile devices
      
      const successful = document.execCommand('copy');
      if (successful) {
        toast({
          title: 'Copied!',
          description: `Order #${text} copied to clipboard`
        });
      } else {
        toast({
          title: 'Copy Failed',
          description: 'Please manually copy the order number',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Fallback copy failed:', error);
      toast({
        title: 'Copy Failed',
        description: 'Please manually copy the order number',
        variant: 'destructive'
      });
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const reprintReceipt = async () => {
    try {
      const available = await unifiedPrintingService.isAvailable();
      if (!available) {
        toast({
          title: 'Printer Not Available',
          description: 'Please ensure QZ Tray is running',
          variant: 'destructive'
        });
        return;
      }

      const receiptSettingsStr = localStorage.getItem('receipt_settings');
      const receiptSettings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {
        showLogo: true,
        showPhone: true,
        showEmail: true,
        leftMargin: 0,
        rightMargin: 0
      };
      
      // Get invoice items - check multiple possible locations
      const invoiceItems = invoice.invoiceItems || invoice.items || [];
      
      if (!invoiceItems || invoiceItems.length === 0) {
        toast({
          title: 'Cannot Print',
          description: 'No items found in this transaction',
          variant: 'destructive'
        });
        return;
      }
      
      const receiptData = {
        receipt_id: invoice._id || invoice.id || invoiceId,
        customer_name: selectedCustomer?.name || invoice.contactDetails?.name || 'Walk-in Customer',
        items: invoiceItems.map(item => ({
          name: item.name || 'Unknown Item',
          variantName: item.variantName || null,
          quantity: parseFloat(item.qty || item.quantity || 1) || 1,
          price: parseFloat(item.amount || item.price || 0) || 0,
          total: parseFloat((item.qty || item.quantity || 1) * (item.amount || item.price || 0)) || 0
        })),
        subtotal: parseFloat(invoice.subtotal || 0) || 0,
        discount: parseFloat(invoice.discountTotal || invoice.discount || 0) || 0,
        tax: parseFloat(invoice.tax || 0) || 0,
        total: parseFloat(invoice.total || invoice.amountDue || invoiceTotal) || 0,
        payment_method: 'Card',
        cc_fee: ccFeeAmount > 0 ? ccFeeAmount : null,
        cc_fee_rate: ccFeeAmount > 0 ? ccFeeRate : null,
        settings: receiptSettings
      };

      console.log('[Reprint] Sending receipt data:', receiptData);

      const { data: htmlData } = await api.post('/receipt/generate', receiptData);
      
      console.log('[Reprint] Received response:', htmlData);
      
      if (!htmlData || !htmlData.receipt_html) {
        throw new Error('No HTML received from receipt generation');
      }
      
      // Use printReceipt method with receipt_html (not html)
      await unifiedPrintingService.printReceipt(htmlData.receipt_html, receiptSettings);
      
      toast({
        title: 'Receipt Sent to Printer',
        description: 'Receipt is being printed'
      });
    } catch (error) {
      console.error('Failed to reprint receipt:', error);
      console.error('Error response:', error.response);
      
      // ALWAYS ensure we have a string, never an object
      let errorMsg = 'Could not print receipt';
      
      try {
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          
          // If detail is an array of validation errors
          if (Array.isArray(detail)) {
            errorMsg = detail.map(err => {
              try {
                const field = err.loc ? err.loc.join('.') : 'field';
                const msg = err.msg || 'error';
                return `${field}: ${msg}`;
              } catch {
                return 'validation error';
              }
            }).join(', ');
          } 
          // If detail is a string
          else if (typeof detail === 'string') {
            errorMsg = detail;
          }
          // If detail is an object
          else if (typeof detail === 'object' && detail !== null) {
            errorMsg = detail.msg || 'Validation error occurred';
          }
        } else if (error.message) {
          errorMsg = String(error.message);
        }
      } catch (parseError) {
        console.error('Error parsing error message:', parseError);
        errorMsg = 'An error occurred while printing';
      }
      
      // Final safety check - ensure it's a string
      if (typeof errorMsg !== 'string') {
        errorMsg = 'Print failed - unknown error';
      }
      
      console.log('Final error message:', errorMsg);
      
      toast({
        title: 'Print Failed',
        description: String(errorMsg),
        variant: 'destructive'
      });
    }
  };

  // Reopen cash drawer function (for after initial open)
  const reopenCashDrawer = async () => {
    try {
      await unifiedPrintingService.initialize();
      
      if (unifiedPrintingService.isNative) {
        // Android native
        console.log('[Cash Drawer] Reopening via native service...');
        await unifiedPrintingService.activeService.openCashDrawer();
      } else {
        // QZ Tray - let it use default printer if none specified
        const receiptSettingsStr = localStorage.getItem('receipt_settings');
        const settings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {};
        const selectedPrinter = settings.selectedPrinter || null;
        console.log('[Cash Drawer] Reopening via QZ Tray, printer:', selectedPrinter || 'default');
        await unifiedPrintingService.openCashDrawer(selectedPrinter);
      }
      
      toast({
        title: 'Cash Drawer Opened',
        description: 'Drawer has been reopened'
      });
    } catch (error) {
      console.error('[Cash Drawer] Reopen error:', error);
      toast({
        title: 'Failed to Open Drawer',
        description: error.message || 'Could not open cash drawer',
        variant: 'destructive'
      });
    }
  };

  // Reprint receipt for cash/check payments (uses current payment info)
  const reprintCashCheckReceipt = async () => {
    try {
      const available = await unifiedPrintingService.isAvailable();
      if (!available) {
        toast({
          title: 'Printer Not Available',
          description: 'Please ensure QZ Tray is running',
          variant: 'destructive'
        });
        return;
      }

      const receiptSettingsStr = localStorage.getItem('receipt_settings');
      const receiptSettings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {};
      
      const invoiceItems = invoice.invoiceItems || invoice.items || [];
      
      if (!invoiceItems || invoiceItems.length === 0) {
        toast({
          title: 'Cannot Print',
          description: 'No items found in this transaction',
          variant: 'destructive'
        });
        return;
      }
      
      const receiptData = {
        receipt_id: createdInvoiceId || invoice._id || invoice.id || invoiceId,
        customer_name: selectedCustomer?.name || invoice.contactDetails?.name || 'Walk-in Customer',
        items: invoiceItems.map(item => ({
          name: item.name || 'Unknown Item',
          variantName: item.variantName || null,
          quantity: parseFloat(item.qty || item.quantity || 1) || 1,
          price: parseFloat(item.amount || item.price || 0) || 0,
          total: parseFloat((item.qty || item.quantity || 1) * (item.amount || item.price || 0)) || 0
        })),
        subtotal: parseFloat(invoice.subtotal || 0) || 0,
        discount: parseFloat(invoice.discountTotal || invoice.discount || 0) || 0,
        tax: parseFloat(invoice.tax || 0) || 0,
        total: parseFloat(invoice.total || invoice.amountDue || invoiceTotal) || 0,
        payment_method: paymentMethod === 'cash' ? 'Cash' : 'Check',
        cash_tendered: paymentMethod === 'cash' ? cashAmount : null,
        change_due: paymentMethod === 'cash' ? changeOwed : null,
        check_number: paymentMethod === 'check' ? checkNumber : null,
        cc_fee: ccFeeAmount > 0 ? ccFeeAmount : null,
        cc_fee_rate: ccFeeAmount > 0 ? ccFeeRate : null,
        settings: receiptSettings
      };

      const { data: htmlData } = await api.post('/receipt/generate', receiptData);
      
      if (!htmlData || !htmlData.receipt_html) {
        throw new Error('No HTML received from receipt generation');
      }
      
      await unifiedPrintingService.printReceipt(htmlData.receipt_html, receiptSettings);
      
      toast({
        title: 'Receipt Sent to Printer',
        description: 'Receipt is being reprinted'
      });
    } catch (error) {
      console.error('Failed to reprint receipt:', error);
      toast({
        title: 'Reprint Failed',
        description: error.message || 'Could not reprint receipt',
        variant: 'destructive'
      });
    }
  };

  const openActivateRewardsDialog = (customer) => {
    setRewardsForm({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address1: customer.address1 || customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      country: customer.country || '',
      postalCode: customer.postalCode || customer.postal_code || customer.zip || ''
    });
    setActivateRewardsDialogOpen(true);
  };

  // Save and activate rewards
  const saveAndActivateRewards = async () => {
    if (!rewardsForm.firstName || !rewardsForm.phone) {
      toast({
        title: 'Required Fields',
        description: 'First name and phone number are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Build custom fields dynamically from settings
      const customFields = {};
      if (rewardsSettings.checkboxFieldId) {
        customFields[rewardsSettings.checkboxFieldId] = rewardsSettings.checkboxOptionValue || 'Yes';
      }
      if (rewardsSettings.dateFieldId) {
        customFields[rewardsSettings.dateFieldId] = new Date().toISOString();
      }

      const updatePayload = {
        firstName: rewardsForm.firstName,
        lastName: rewardsForm.lastName,
        phone: rewardsForm.phone,
        email: rewardsForm.email,
        address1: rewardsForm.address1,
        city: rewardsForm.city,
        state: rewardsForm.state,
        country: rewardsForm.country,
        postalCode: rewardsForm.postalCode,
        customField: customFields
      };
      
      await api.patch(`/live/contacts/${selectedCustomer.id}`, updatePayload, { 
        headers: { 'X-Use-Live': 'true' } 
      });
      
      toast({
        title: 'Rewards Activated!',
        description: `${rewardsForm.firstName} ${rewardsForm.lastName} is now a rewards member.`,
      });
      
      // Update the selected customer to reflect the change
      const updatedCustomer = { 
        ...selectedCustomer,
        firstName: rewardsForm.firstName,
        lastName: rewardsForm.lastName,
        phone: rewardsForm.phone,
        email: rewardsForm.email,
        name: `${rewardsForm.firstName} ${rewardsForm.lastName}`.trim()
      };
      if (!updatedCustomer.customFields) updatedCustomer.customFields = [];
      updatedCustomer.customFields.push({
        id: '3BCupt6aEd8JCatV2fc5',
        key: 'contact.activate_rewards_member',
        field_value: ['Yes']
      });
      setSelectedCustomer(updatedCustomer);
      setActivateRewardsDialogOpen(false);
      
    } catch (error) {
      console.error('Activation failed:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to activate rewards. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleCustomerSelect = async (customer) => {
    // Format customer name properly
    const customerWithName = {
      ...customer,
      name: customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
    };
    
    setSelectedCustomer(customerWithName);
    setCustomerSearch('');
    setSearchResults([]);

    // Only update invoice if it's an existing invoice (not from cart)
    // If coming from cart, invoice will be created on payment with customer details
    if (invoiceId !== 'new' && !location.state?.cartData && invoice) {
      try {
        // Build full invoice update payload (GHL API requires full structure for PUT)
        const updatePayload = {
          altId: invoice.altId,
          altType: invoice.altType,
          name: invoice.name,
          currency: invoice.currency,
          contactId: customer.id,
          contactDetails: {
            id: customer.id,
            name: customer.name || `${customer.firstName} ${customer.lastName}`,
            email: customer.email || '',
            phoneNo: customer.phone || ''
          },
          invoiceItems: invoice.invoiceItems || [],
          issueDate: invoice.issueDate,
          dueDate: invoice.dueDate
        };

        await api.put(`/live/invoices/${invoiceId}`, updatePayload, { params: { live: 1 } });

        toast({
          title: 'Customer Updated',
          description: `${customer.name || customer.firstName} assigned to invoice`
        });

        // Refresh invoice
        fetchInvoice();
      } catch (error) {
        console.error('Failed to update invoice:', error);
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Failed to update customer on invoice',
          variant: 'destructive'
        });
      }
    }
    // Note: No toast notification for cart - customer is visually selected in UI
  };

  const handleAddNewCustomer = async () => {
    if (!newCustomer.firstName || !newCustomer.phone) {
      toast({
        title: 'Required Fields',
        description: 'First name and phone number are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Prepare contact data
      const contactData = {
        firstName: newCustomer.firstName,
        lastName: newCustomer.lastName,
        phone: newCustomer.phone,
        email: newCustomer.email
      };
      
      // Add address if provided
      if (newCustomer.address1) {
        contactData.address1 = newCustomer.address1;
      }
      if (newCustomer.city) {
        contactData.city = newCustomer.city;
      }
      if (newCustomer.state) {
        contactData.state = newCustomer.state;
      }
      if (newCustomer.postalCode) {
        contactData.postalCode = newCustomer.postalCode;
      }
      if (newCustomer.country) {
        contactData.country = newCustomer.country;
      }
      
      // Create new contact in GHL
      const { data } = await api.post('/live/contacts', contactData, { 
        headers: { 'X-Use-Live': 'true' } 
      });

      const createdContact = data.contact || data;
      
      // If rewards is enabled AND toggle is ON, enroll in rewards
      if (rewardsEnabled && enableRewardsForNewCustomer && rewardsSettings?.customFields) {
        try {
          const enrollData = {
            customFields: {}
          };
          
          // Set rewards activation status
          if (rewardsSettings.customFields.memberStatus) {
            enrollData.customFields[rewardsSettings.customFields.memberStatus] = 'Active';
          }
          
          // Set initial points to 0
          if (rewardsSettings.customFields.pointsBalance) {
            enrollData.customFields[rewardsSettings.customFields.pointsBalance] = '0';
          }
          
          // Set enrollment date
          if (rewardsSettings.customFields.enrollmentDate) {
            enrollData.customFields[rewardsSettings.customFields.enrollmentDate] = new Date().toISOString().split('T')[0];
          }
          
          await api.patch(`/live/contacts/${createdContact.id}`, enrollData, {
            headers: { 'X-Use-Live': 'true' }
          });
          
          toast({
            title: 'Customer Created & Enrolled',
            description: `${newCustomer.firstName} ${newCustomer.lastName} enrolled in rewards program`
          });
        } catch (rewardsError) {
          console.error('Failed to enroll in rewards:', rewardsError);
          toast({
            title: 'Customer Created',
            description: `${newCustomer.firstName} ${newCustomer.lastName} created, but rewards enrollment failed`,
            variant: 'destructive'
          });
        }
      } else {
        toast({
          title: 'Customer Created',
          description: `${newCustomer.firstName} ${newCustomer.lastName} enrolled successfully`
        });
      }

      // Select the newly created customer
      await handleCustomerSelect({
        id: createdContact.id,
        name: `${newCustomer.firstName} ${newCustomer.lastName}`,
        firstName: newCustomer.firstName,
        lastName: newCustomer.lastName,
        email: newCustomer.email,
        phone: newCustomer.phone
      });

      // Reset form and close dialog
      setNewCustomer({ 
        firstName: '', 
        lastName: '', 
        phone: '', 
        email: '',
        address1: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US'
      });
      setEnableRewardsForNewCustomer(true); // Reset to default
      setAddCustomerDialogOpen(false);
    } catch (error) {
      console.error('Failed to create customer:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create customer',
        variant: 'destructive'
      });
    }
  };

  const finishPaymentFlow = () => {
    // Clear the cart
    clear();
    
    // Show success overlay
    setShowSuccessOverlay(true);
    setPaymentSuccess(true);
    setShowTerminalDialog(false);
    
    // Wait 2 seconds to show success screen, then navigate back to dashboard
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };

  const handleProcessPaymentClick = () => {
    // Open payment method selection modal (don't create invoice yet)
    setPaymentDialogOpen(true);
  };

  const handlePaymentModalClose = async (paymentResult) => {
    setShowPaymentModal(false);
    
    if (paymentResult) {
      // Stripe payment succeeded — now create GHL invoice + record payment
      try {
        let finalInvoiceId = pendingInvoiceId || createdInvoiceId || invoiceId;
        
        // Step 1: Create the GHL invoice if needed (includes CC fee as line item)
        if (invoiceId === 'new' && !createdInvoiceId && location.state?.cartData) {
          const cartData = location.state.cartData;
          const invoicePayload = {
            customerId: selectedCustomer?.id || cartData.contactId,
            items: cartData.items,
            discount: cartData.discount,
            taxRate: cartData.taxRate,
            tax: cartData.tax,
            total: cartData.total + ccFeeAmount,
            ccFeeAmount: ccFeeAmount,
            ccFeeRate: ccFeeRate
          };
          
          console.log('[ManualEntry] Creating GHL invoice after Stripe success...');
          const { data } = await api.post('/live/invoices', invoicePayload, { params: { live: 1 } });
          finalInvoiceId = data._id || data.id;
          setCreatedInvoiceId(finalInvoiceId);
          setPendingInvoiceId(finalInvoiceId);
          
          setInvoice(prev => ({
            ...prev,
            ...data,
            total: data.total || data.amountDue,
            amountDue: data.amountDue || data.total
          }));
          
          console.log('[ManualEntry] GHL invoice created:', finalInvoiceId, 'AmountDue:', data.amountDue);
        }
        
        // Step 2: Record the payment in GHL
        if (finalInvoiceId && finalInvoiceId !== 'new') {
          console.log('[ManualEntry] Recording payment in GHL:', finalInvoiceId, 'Amount:', paymentResult.amount);
          await api.post('/stripe/record-payment', {
            invoice_id: finalInvoiceId,
            payment_intent_id: paymentResult.paymentIntentId,
            amount: paymentResult.amount || invoiceTotal
          });
          console.log('[ManualEntry] Payment recorded successfully in GHL');
        }
      } catch (ghlError) {
        console.error('[ManualEntry] GHL invoice/recording error:', ghlError);
        // Payment was collected via Stripe — warn but don't block
        toast({
          title: 'Payment Collected',
          description: 'Card charged successfully, but failed to sync with GHL. The invoice may need manual updating.',
          variant: 'warning'
        });
      }
      
      // Print receipt if available
      if (qzTrayAvailable) {
        const receiptSettingsStr = localStorage.getItem('receipt_settings');
        const receiptSettings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {};
        const invoiceItems = invoice.invoiceItems || invoice.items || [];
        
        const receiptData = {
          receipt_id: finalInvoiceId,
          customer_name: selectedCustomer?.name || 'Walk-in Customer',
          items: invoiceItems.map(item => ({
            name: item.name,
            variantName: item.variantName,
            quantity: item.qty || item.quantity || 1,
            price: item.amount || item.price || 0,
            total: (item.qty || item.quantity || 1) * (item.amount || item.price || 0)
          })),
          subtotal: invoice.subtotal || 0,
          discount: invoice.discountTotal || 0,
          tax: invoice.tax || 0,
          total: invoiceTotal,
          payment_method: 'Credit Card (Manual Entry)',
          cc_fee: ccFeeAmount > 0 ? ccFeeAmount : null,
          cc_fee_rate: ccFeeAmount > 0 ? ccFeeRate : null,
          settings: receiptSettings
        };
        
        if (allowCompleteWithoutPrinting) {
          setPendingReceiptData(receiptData);
          setPrintPromptOpen(true);
          return; // The prompt modal will trigger finishPaymentFlow
        } else {
          await printReceipt(receiptData);
        }
      }

      finishPaymentFlow();
    }
    // If not successful, just close the modal and let user try again
  };

  const printReceipt = async (receiptData, options = {}) => {
    setPrinting(true);
    
    try {
      console.log('[Print] Starting print process...');
      console.log('[Print] Receipt data:', receiptData);
      
      // Initialize printing service
      console.log('[Print] Initializing printing service...');
      await unifiedPrintingService.initialize();
      console.log('[Print] Printing service initialized successfully');
      
      // Generate receipt HTML from backend
      console.log('[Print] Generating receipt HTML from backend...');
      const { data } = await api.post('/receipt/generate', receiptData);
      
      if (!data.success) {
        throw new Error('Failed to generate receipt');
      }
      
      console.log('[Print] Receipt generated successfully');
      console.log('[Print] Receipt HTML length:', data.receipt_html?.length);
      
      // Get printer settings
      const receiptSettingsStr = localStorage.getItem('receipt_settings');
      const settings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {};
      const selectedPrinter = settings.selectedPrinter || null;
      
      // Prepare margins for printing (only left/right - printer handles top/bottom)
      const margins = {
        top: 0,  // Printer handles top spacing
        left: settings.leftMargin || settings.sideMargin || 0,
        right: settings.rightMargin || settings.sideMargin || 0,
        bottom: 0  // Printer handles bottom spacing
      };
      
      console.log('[Print] Printer settings:', {
        selectedPrinter,
        openCashDrawer: settings.openCashDrawer,
        thankYouMessage: settings.thankYouMessage,
        margins
      });
      
      // Print receipt
      console.log('[Print] Sending to printer:', selectedPrinter || 'default');
      await unifiedPrintingService.printReceipt(data.receipt_html, {
        printerName: selectedPrinter,
        businessName: receiptData.business_name,
        footer: settings.thankYouMessage || 'Thank you for your business!',
        margins
      });
      console.log('[Print] Print job sent successfully');
      
      // Open cash drawer if needed (only if not manually opened already)
      const openDrawer = settings.openCashDrawer !== false && !options.skipDrawerKick;
      const isCashOrCheck = receiptData.payment_method === 'cash' || receiptData.payment_method === 'check';
      
      if (openDrawer && isCashOrCheck) {
        console.log('[Print] Opening cash drawer...');
        try {
          await unifiedPrintingService.openCashDrawer(selectedPrinter);
          console.log('[Print] Cash drawer opened');
        } catch (drawerError) {
          console.error('[Print] Cash drawer error:', drawerError);
        }
      }
      
      toast({
        title: 'Receipt Printed',
        description: 'Receipt printed successfully'
      });
      
      return true;
    } catch (error) {
      console.error('[Print] Failed to print receipt:', error);
      toast({
        title: 'Print Failed',
        description: error.message || 'Could not print receipt',
        variant: 'destructive'
      });
      return false;
    } finally {
      setPrinting(false);
    }
  };

  const openDrawerAndPrint = async () => {
    setPrinting(true);
    
    try {
      let finalInvoiceId = invoiceId;
      
      // Check if invoice was already created
      if (createdInvoiceId && invoiceId === 'new') {
        finalInvoiceId = createdInvoiceId;
      }
      // If this is a new invoice (from cart) and not already created, create it now
      else if (invoiceId === 'new' && location.state?.cartData) {
        const cartData = location.state.cartData;
        
        const invoicePayload = {
          customerId: selectedCustomer?.id || cartData.contactId,
          items: cartData.items,
          discount: cartData.discount,
          taxRate: cartData.taxRate,
          tax: cartData.tax,
          total: cartData.total
        };
        
        const { data } = await api.post('/live/invoices', invoicePayload, { params: { live: 1 } });
        finalInvoiceId = data._id || data.id;
        
        if (!finalInvoiceId) {
          throw new Error('No invoice ID returned from API');
        }
        
        setCreatedInvoiceId(finalInvoiceId);
        setPendingInvoiceId(finalInvoiceId);
        
        setInvoice(prev => ({
          ...prev,
          ...data,
          total: data.total || data.amountDue,
          amountDue: data.amountDue || data.total
        }));
      }
      
      const actualInvoiceId = finalInvoiceId || createdInvoiceId;
      
      // 1. OPEN CASH DRAWER FIRST
      console.log('[Workflow] Step 1: Opening cash drawer...');
      await unifiedPrintingService.initialize();
      const receiptSettingsStr = localStorage.getItem('receipt_settings');
      const settings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {};
      const selectedPrinter = settings.selectedPrinter || null;
      
      if (unifiedPrintingService.isNative) {
        // Android native
        console.log('[Cash Drawer] Opening via native service...');
        await unifiedPrintingService.activeService.openCashDrawer();
      } else {
        // QZ Tray - pass printer name (or null for default)
        console.log('[Cash Drawer] Opening via QZ Tray, printer:', selectedPrinter || 'default');
        await unifiedPrintingService.openCashDrawer(selectedPrinter);
      }
      
      toast({
        title: 'Cash Drawer Opened',
        description: 'Please give change to customer'
      });
      
      // 2. PRINT RECEIPT SECOND
      console.log('[Workflow] Step 2: Printing receipt...');
      if (qzTrayAvailable) {
        const invoiceItems = invoice.invoiceItems || invoice.items || [];
        const receiptSettings = settings;
        
        const receiptData = {
          receipt_id: actualInvoiceId,
          customer_name: selectedCustomer?.name || 'Walk-in Customer',
          items: invoiceItems.map(item => ({
            name: item.name,
            variantName: item.variantName,
            quantity: item.qty || item.quantity || 1,
            price: item.amount || item.price || 0,
            total: (item.qty || item.quantity || 1) * (item.amount || item.price || 0)
          })),
          subtotal: invoice.subtotal || 0,
          discount: invoice.discountTotal || 0,
          tax: invoice.tax || 0,
          total: invoiceTotal,
          payment_method: paymentMethod,
          cash_tendered: paymentMethod === 'cash' ? cashAmount : null,
          change_due: paymentMethod === 'cash' ? changeOwed : null,
          check_number: paymentMethod === 'check' ? checkNumber : null,
          cc_fee: ccFeeAmount > 0 ? ccFeeAmount : null,
          cc_fee_rate: ccFeeAmount > 0 ? ccFeeRate : null,
          settings: receiptSettings
        };
        
        const printSuccess = await printReceipt(receiptData, { skipDrawerKick: true });
        
        if (printSuccess) {
          toast({
            title: 'Receipt Printed',
            description: 'Receipt printed successfully'
          });
        } else {
          toast({
            title: 'Print Failed',
            description: 'Receipt printing failed, but drawer is open',
            variant: 'destructive'
          });
        }
      }
      
      // Mark as done
      setDrawerOpenedAndPrinted(true);
      
      toast({
        title: 'Ready',
        description: 'Give change, then click Complete to finish transaction',
        duration: 5000
      });
      
    } catch (error) {
      console.error('[Workflow] Error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to open drawer or print receipt',
        variant: 'destructive'
      });
    } finally {
      setPrinting(false);
    }
  };

  const processTerminalPayment = async () => {
    setTerminalProcessing(true);
    setTerminalStatus('Preparing...');
    try {
      let finalInvoiceId = invoiceId;
      
      // Create or reuse invoice
      if (createdInvoiceId && invoiceId === 'new') {
        finalInvoiceId = createdInvoiceId;
        setPendingInvoiceId(createdInvoiceId);
      } else if (invoiceId === 'new' && location.state?.cartData) {
        const cartData = location.state.cartData;
        
        const invoicePayload = {
          customerId: selectedCustomer?.id || cartData.contactId,
          items: cartData.items,
          discount: cartData.discount,
          taxRate: cartData.taxRate,
          tax: cartData.tax,
          total: cartData.total + ccFeeAmount,
          ccFeeAmount: ccFeeAmount,
          ccFeeRate: ccFeeRate
        };
        
        setTerminalStatus('Creating invoice...');
        const { data } = await api.post('/live/invoices', invoicePayload, { params: { live: 1 } });
        finalInvoiceId = data._id || data.id;
        setCreatedInvoiceId(finalInvoiceId);
        setPendingInvoiceId(finalInvoiceId);
        
        setInvoice(prev => ({
          ...prev,
          ...data,
          total: data.total || data.amountDue,
          amountDue: data.amountDue || data.total
        }));
      } else {
        setPendingInvoiceId(invoiceId);
      }
      
      // Process payment with Terminal
      setTerminalStatus('Insert, tap, or swipe card...');
      const paymentResult = await stripeTerminal.processPayment(invoiceTotal, finalInvoiceId);
      
      if (paymentResult.success) {
        setTerminalStatus('Recording payment...');
        
        const isNewInvoice = invoiceId === 'new' || createdInvoiceId;
        const paymentRecord = {
          amount: isNewInvoice ? invoiceTotal : baseInvoiceTotal, // Send full amount if we added the fee line item, else base amount to prevent GHL 400 error
          paymentMode: 'Credit Card',
          note: `Card payment via Stripe Terminal\nCard: ${paymentResult.cardBrand} ****${paymentResult.cardLast4}\nPayment ID: ${paymentResult.paymentIntentId}${ccFeeAmount > 0 ? `\nCC Fee (${(ccFeeRate * 100).toFixed(1)}%): $${ccFeeAmount.toFixed(2)}` : ''}`
        };
        
        await api.post(`/live/invoices/${finalInvoiceId}/record-payment`, paymentRecord, { params: { live: 1 } });
        
        // Print receipt if available
        if (qzTrayAvailable) {
          const receiptSettingsStr = localStorage.getItem('receipt_settings');
          const receiptSettings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {};
          const invoiceItems = invoice.invoiceItems || invoice.items || [];
          
          const receiptData = {
            receipt_id: finalInvoiceId,
            customer_name: selectedCustomer?.name || 'Walk-in Customer',
            items: invoiceItems.map(item => ({
              name: item.name,
              variantName: item.variantName,
              quantity: item.qty || item.quantity || 1,
              price: item.amount || item.price || 0,
              total: (item.qty || item.quantity || 1) * (item.amount || item.price || 0)
            })),
            subtotal: invoice.subtotal || 0,
            discount: invoice.discountTotal || 0,
            tax: invoice.tax || 0,
            total: invoiceTotal,
            payment_method: `Card (${paymentResult.cardBrand} ****${paymentResult.cardLast4})`,
            cc_fee: ccFeeAmount > 0 ? ccFeeAmount : null,
            cc_fee_rate: ccFeeAmount > 0 ? ccFeeRate : null,
            settings: receiptSettings
          };
          
          if (allowCompleteWithoutPrinting) {
            setPendingReceiptData(receiptData);
            setPrintPromptOpen(true);
            toast({
              title: 'Payment Successful!',
              description: `Card payment of $${invoiceTotal.toFixed(2)} processed`
            });
            return; // The prompt modal will trigger finishPaymentFlow
          } else {
            await printReceipt(receiptData);
          }
        }
        
        toast({
          title: 'Payment Successful!',
          description: `Card payment of $${invoiceTotal.toFixed(2)} processed`
        });
        finishPaymentFlow();
      }
    } catch (error) {
      console.error('Terminal payment failed:', error);
      setTerminalStatus('Payment failed');
      setTerminalConnectionFailed(true);
      
      toast({
        title: 'Payment Failed',
        description: error.message || 'Could not process card payment',
        variant: 'destructive'
      });
    } finally {
      setTerminalProcessing(false);
    }
  };

  const completePayment = async (options = {}) => {
    // Note: React onClick passes a synthetic event. If options has nativeEvent, it's an event, not our options object.
    const isSyntheticEvent = options && options.nativeEvent;
    const skipPrint = !isSyntheticEvent && options && options.skipPrint === true;
    
    setProcessing(true);
    
    try {
      let finalInvoiceId = invoiceId;
      
      // Check if invoice was already created (e.g., user clicked Card first)
      if (createdInvoiceId && invoiceId === 'new') {
        console.log('[PAYMENT] Reusing existing invoice:', createdInvoiceId);
        finalInvoiceId = createdInvoiceId;
      }
      // If this is a new invoice (from cart) and not already created, create it now
      else if (invoiceId === 'new' && location.state?.cartData) {
        const cartData = location.state.cartData;
        
        // Build invoice payload with customer if selected
        const invoicePayload = {
          customerId: selectedCustomer?.id || cartData.contactId,
          items: cartData.items,
          discount: cartData.discount,
          taxRate: cartData.taxRate,
          tax: cartData.tax,
          total: cartData.total + ccFeeAmount,
          ccFeeAmount: ccFeeAmount,
          ccFeeRate: ccFeeRate
        };
        
        toast({
          title: 'Creating Invoice',
          description: 'Creating invoice in your CRM...'
        });
        
        // Create invoice in GHL with automatic tax calculation
        const { data } = await api.post('/live/invoices', invoicePayload, { params: { live: 1 } });
        finalInvoiceId = data._id || data.id;
        
        if (!finalInvoiceId) {
          throw new Error('No invoice ID returned from API');
        }
        
        // Check for tax mismatch warning
        if (data._taxMismatchWarning) {
          const warning = data._taxMismatchWarning;
          toast({
            title: 'Tax Rate Mismatch Warning',
            description: warning.message,
            variant: 'destructive',
            duration: 10000
          });
          console.warn('[TAX MISMATCH]', warning);
        }
        
        // Store invoice ID
        setCreatedInvoiceId(finalInvoiceId);
        setPendingInvoiceId(finalInvoiceId);
        
        // Update invoice state with GHL returned total
        setInvoice(prev => ({
          ...prev,
          ...data,
          total: data.total || data.amountDue,
          amountDue: data.amountDue || data.total
        }));
        
        toast({
          title: 'Invoice Created',
          description: `Invoice #${data.invoiceNumber || finalInvoiceId} created successfully`
        });
      } else {
        setPendingInvoiceId(invoiceId);
      }
      
      // Use the actual invoice ID, not the URL param which might be "new"
      const actualInvoiceId = finalInvoiceId || createdInvoiceId;
      
      console.log('Recording payment for invoice:', actualInvoiceId);
      
      if (!actualInvoiceId || actualInvoiceId === 'new') {
        throw new Error('Invalid invoice ID - cannot record payment');
      }
      
      // Build payment record payload for GHL
      const isNewInvoice = invoiceId === 'new' || createdInvoiceId;
      const paymentRecord = {
        amount: isNewInvoice ? invoiceTotal : baseInvoiceTotal, // Send full amount if we added the fee line item, else base amount to prevent GHL 400 error
        paymentMode: paymentMethod === 'cash' ? 'Cash' : paymentMethod === 'card' ? 'Credit Card' : 'Check',
        note: paymentMethod === 'cash' 
          ? `Cash tendered: $${cashAmount.toFixed(2)}, Change: $${changeOwed.toFixed(2)}`
          : paymentMethod === 'check'
          ? `Check #${checkNumber}`
          : 'Card payment via Stripe Terminal'
      };
      
      console.log('Payment record payload:', paymentRecord);
      console.log('Invoice total from state:', invoiceTotal);
      console.log('Invoice object:', invoice);
      
      // Print receipt (only if not already printed AND not skipping)
      if (qzTrayAvailable && !drawerOpenedAndPrinted && !skipPrint) {
        // Get items from invoice object
        const invoiceItems = invoice.invoiceItems || invoice.items || [];
        
        // Get receipt settings from localStorage
        const receiptSettingsStr = localStorage.getItem('receipt_settings');
        const receiptSettings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {
          showPhone: true,
          showEmail: true,
          showBarcode: true,
          topMargin: 0,  // Printer handles top spacing
          bottomMargin: 0,  // Printer handles bottom spacing
          leftMargin: 5,
          rightMargin: 5,
          sideMargin: 5  // Legacy field
        };
        
        const receiptData = {
          receipt_id: actualInvoiceId,
          customer_name: selectedCustomer?.name || 'Walk-in Customer',
          items: invoiceItems.map(item => ({
            name: item.name,
            variantName: item.variantName,
            quantity: item.qty || item.quantity || 1,
            price: item.amount || item.price || 0,
            total: (item.qty || item.quantity || 1) * (item.amount || item.price || 0)
          })),
          subtotal: invoice.subtotal || 0,
          discount: invoice.discountTotal || 0,
          tax: invoice.tax || 0,
          total: invoiceTotal,
          payment_method: paymentMethod,
          cash_tendered: paymentMethod === 'cash' ? cashAmount : null,
          change_due: paymentMethod === 'cash' ? changeOwed : null,
          check_number: paymentMethod === 'check' ? checkNumber : null,
          cc_fee: ccFeeAmount > 0 ? ccFeeAmount : null,
          cc_fee_rate: ccFeeAmount > 0 ? ccFeeRate : null,
          settings: receiptSettings
        };
        
        console.log('Printing receipt before payment...');
        const printSuccess = await printReceipt(receiptData);
        
        if (!printSuccess) {
          // Ask user if they want to continue without printing
          const continueWithoutPrint = window.confirm(
            'Receipt printing failed. Do you want to continue with payment anyway?'
          );
          if (!continueWithoutPrint) {
            setProcessing(false);
            return;
          }
        }
      } else {
        console.warn('QZ Tray not available, skipping print');
      }
      
      // Record manual payment in GHL using the correct endpoint
      const paymentResponse = await api.post(`/live/invoices/${actualInvoiceId}/record-payment`, paymentRecord, { params: { live: 1 } });
      console.log('Payment recorded:', paymentResponse.data);
      
      // Clear the cart
      clear();
      
      // Show success overlay
      setShowSuccessOverlay(true);
      setPaymentSuccess(true);
      
      // Wait 2 seconds to show success screen, then navigate back to dashboard
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (error) {
      console.error('Payment processing failed:', error);
      toast({
        title: 'Payment Failed',
        description: error.response?.data?.detail || 'Could not process payment',
        variant: 'destructive'
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading invoice...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="text-lg">Invoice not found</div>
        <Button onClick={() => navigate('/sale')}>Return to Sales</Button>
      </div>
    );
  }

  // Handle both cart data (new) and existing invoice
  const isNewInvoice = invoiceId === 'new';
  const isPaidInvoice = invoice.status === 'paid';
  const items = invoice.invoiceItems || invoice.items || [];

  return (
    <>
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              if (isPaidInvoice) {
                // For paid invoices, go back to dashboard
                navigate('/');
              } else if (invoice.status === 'draft') {
                // For draft invoices, load items into cart and go to sale page
                const draftItems = invoice.invoiceItems || invoice.items || [];
                navigate('/sale', { 
                  state: { 
                    selectedCustomer,
                    draftInvoice: invoice,
                    loadItems: draftItems
                  } 
                });
              } else {
                // Default: go to sale page
                navigate('/sale', { state: { selectedCustomer } });
              }
            }}
            className="h-14 px-6 text-base font-semibold"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {isPaidInvoice ? 'Back' : 'Edit Items'}
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">
              {isNewInvoice ? 'Review & Pay' : isPaidInvoice ? 'View Invoice' : 'Invoice Payment'}
            </h1>
            <p className="text-gray-600">
              {isNewInvoice ? 'Add customer and process payment' : `Invoice #${invoice.invoiceNumber || invoiceId}`}
            </p>
            {isPaidInvoice && (
              <div className="mt-2 inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                <CheckCircle className="h-4 w-4" />
                Paid - View Only
              </div>
            )}
          </div>
          
          {/* Quick Actions - Show for existing invoices */}
          {!isNewInvoice && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={copyInvoiceNumber}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy Order #
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={reprintReceipt}
              >
                <Printer className="h-4 w-4 mr-1" />
                Reprint Receipt
              </Button>
            </div>
          )}
        </div>

        {/* Tablet-optimized layout: Actions at top, cards below */}
        <div className="space-y-6">
          
          {/* Action Buttons Row */}
          {!isPaidInvoice && (
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  // Clear cart and go to sale page
                  navigate('/sale', { state: { clearCart: true } });
                }}
                className="h-14 text-base font-semibold border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleProcessPaymentClick}
                disabled={processing || paymentSuccess}
                size="lg"
                className="h-14 text-xl font-semibold bg-black hover:bg-gray-800 text-white"
              >
                {paymentSuccess ? (
                  <>
                    <CheckCircle className="h-6 w-6 mr-2" />
                    Payment Complete!
                  </>
                ) : (
                  `Process Payment - $${baseInvoiceTotal.toFixed(2)}`
                )}
              </Button>
            </div>
          )}

          {/* Two Column Layout - Invoice Items (Left) & Customer (Right) */}
          <div className="grid gap-6 lg:grid-cols-2">
            
            {/* LEFT: Invoice Items Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Invoice Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-start py-2 border-b">
                      <div>
                        <div className="font-medium">{item.name}</div>
                        {item.variantName && item.variantName !== 'Default' && (
                          <div className="text-sm text-primary">{item.variantName}</div>
                        )}
                        <div className="text-sm text-gray-600">
                          Qty: {item.qty || item.quantity} × ${(item.amount || item.price || 0).toFixed(2)}
                        </div>
                      </div>
                      <div className="font-semibold">
                        ${((item.qty || item.quantity || 1) * (item.amount || item.price || 0)).toFixed(2)}
                      </div>
                    </div>
                  ))}
                  
                  {/* Totals Breakdown */}
                  <div className="pt-3 space-y-2 border-t-2">
                    {/* Subtotal */}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Subtotal</span>
                      <span>${(invoice.subtotal || 0).toFixed(2)}</span>
                    </div>
                    
                    {/* Discounts - Always show */}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Discounts</span>
                      <span>-${(invoice.discountTotal || 0).toFixed(2)}</span>
                    </div>
                    
                    {/* Tax - Always show */}
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Tax {invoice.taxRate ? `(${(invoice.taxRate * 100).toFixed(1)}%)` : '(0.0%)'}</span>
                      <span>${(invoice.tax || 0).toFixed(2)}</span>
                    </div>
                    
                    {/* CC Fee section - always show both totals when enabled */}
                    {/* Standard total display - CC fee is hidden on this page to avoid confusion */}
                    <div className="flex justify-between text-2xl font-bold pt-3 border-t-2 border-gray-900">
                      <span>Total</span>
                      <span>${baseInvoiceTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* RIGHT: Customer Information Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isPaidInvoice && invoice.contactDetails ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-gray-600">Name</Label>
                      <div className="font-semibold">{invoice.contactDetails.name || 'Walk-in Customer'}</div>
                    </div>
                    <div>
                      <Label className="text-gray-600">Email</Label>
                      <div className="font-semibold">{invoice.contactDetails.email || 'N/A'}</div>
                    </div>
                    <div>
                      <Label className="text-gray-600">Phone</Label>
                      <div className="font-semibold">{invoice.contactDetails.phoneNo || 'N/A'}</div>
                    </div>
                  </div>
                ) : (selectedCustomer || (!isPaidInvoice && invoice?.contactDetails)) ? (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-gray-600">Name</Label>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">
                          {selectedCustomer?.name || invoice?.contactDetails?.name || 'Customer'}
                        </span>
                        {rewardsEnabled && selectedCustomer && isRewardsMember(selectedCustomer) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            <BadgeCheck className="h-3 w-3" />
                            Active Member
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-600">Email</Label>
                      <div className="font-semibold">
                        {selectedCustomer?.email || invoice?.contactDetails?.email || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-600">Phone</Label>
                      <div className="font-semibold">
                        {selectedCustomer?.phone || invoice?.contactDetails?.phoneNo || 'N/A'}
                      </div>
                    </div>
                    
                    {rewardsEnabled && selectedCustomer && !isRewardsMember(selectedCustomer) && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => openActivateRewardsDialog(selectedCustomer)}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <BadgeCheck className="h-4 w-4 mr-2" />
                        Activate Rewards
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedCustomer(null)}
                      className="w-full"
                    >
                      Change Customer
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Search results ABOVE search bar to avoid keyboard overlap */}
                    {searchResults.length > 0 && (
                      <div className="border rounded-md max-h-60 overflow-y-auto bg-white shadow-lg mb-2">
                        {searchResults.map((customer) => {
                          const isActive = isRewardsMember(customer);
                          return (
                            <button
                              key={customer.id}
                              onClick={() => handleCustomerSelect(customer)}
                              className="w-full text-left p-3 hover:bg-gray-100 border-b last:border-b-0 transition-colors"
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim()}
                                    </span>
                                    {rewardsEnabled && isActive && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                        <BadgeCheck className="h-3 w-3" />
                                        Active Member
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-600">{customer.email || 'No email'}</div>
                                </div>
                                {customer.phone && (
                                  <div className="text-sm font-medium text-gray-700 ml-4">
                                    {customer.phone}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    
                    <div>
                      <Label>Search for Customer</Label>
                      <Input
                        placeholder="Type name, email, or phone..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="mt-1"
                      />
                      {searching && (
                        <div className="text-sm text-gray-500 mt-1">Searching...</div>
                      )}
                    </div>

                    {customerSearch.length >= 2 && !searching && searchResults.length === 0 && (
                      <div className="text-sm text-gray-600 p-2 bg-gray-50 rounded">
                        No customers found. Try adding a new one below.
                      </div>
                    )}

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-muted-foreground">Or</span>
                      </div>
                    </div>

                    <Dialog open={addCustomerDialogOpen} onOpenChange={setAddCustomerDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full" size="lg">
                          <UserPlus className="h-4 w-4 mr-2" />
                          Add New Customer
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl top-[5%] translate-y-0 max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <div className="flex items-center justify-between">
                            <DialogTitle>Enroll New Customer</DialogTitle>
                            {rewardsEnabled && (
                              <div className="flex items-center gap-2">
                                <Label htmlFor="enable-rewards" className="text-sm font-medium cursor-pointer">
                                  Enable Rewards
                                </Label>
                                <Switch
                                  id="enable-rewards"
                                  checked={enableRewardsForNewCustomer}
                                  onCheckedChange={setEnableRewardsForNewCustomer}
                                />
                              </div>
                            )}
                          </div>
                        </DialogHeader>
                        <div className="py-4">
                          {/* Two Column Layout */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* Left Column */}
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="firstName">First Name *</Label>
                                <Input
                                  id="firstName"
                                  placeholder="John"
                                  value={newCustomer.firstName}
                                  onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="lastName">Last Name</Label>
                                <Input
                                  id="lastName"
                                  placeholder="Doe"
                                  value={newCustomer.lastName}
                                  onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="phone">Phone Number *</Label>
                                <Input
                                  id="phone"
                                  placeholder="+1 (555) 123-4567"
                                  value={newCustomer.phone}
                                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                  id="email"
                                  type="email"
                                  placeholder="john@example.com"
                                  value={newCustomer.email}
                                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                                />
                              </div>
                            </div>
                            
                            {/* Right Column - Address */}
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="address1">Address (Optional)</Label>
                                <Input
                                  id="address1"
                                  placeholder="123 Main St"
                                  value={newCustomer.address1}
                                  onChange={(e) => setNewCustomer({ ...newCustomer, address1: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="city">City</Label>
                                <Input
                                  id="city"
                                  placeholder="New York"
                                  value={newCustomer.city}
                                  onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-2">
                                  <Label htmlFor="state">State</Label>
                                  <Input
                                    id="state"
                                    placeholder="NY"
                                    value={newCustomer.state}
                                    onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value })}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="postalCode">Zip Code</Label>
                                  <Input
                                    id="postalCode"
                                    placeholder="10001"
                                    value={newCustomer.postalCode}
                                    onChange={(e) => setNewCustomer({ ...newCustomer, postalCode: e.target.value })}
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="country">Country</Label>
                                <Input
                                  id="country"
                                  placeholder="US"
                                  value={newCustomer.country}
                                  onChange={(e) => setNewCustomer({ ...newCustomer, country: e.target.value })}
                                />
                              </div>
                            </div>
                          </div>
                          
                          <Button onClick={handleAddNewCustomer} className="w-full mt-6" size="lg">
                            Enroll Customer
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <div className="text-sm text-gray-600 text-center">
                      Or continue with walk-in customer
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
        </div>

        </div>

        {/* Payment Method Selection & Specific Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={(open) => {
          // Simple guard: don't process close events during/after successful payment
          if (paymentSuccess || processing) {
            return;
          }
          
          setPaymentDialogOpen(open);
          if (!open) {
            // Clean up invoice memory when dialog is closed
            setCreatedInvoiceId(null);
            setPendingInvoiceId(null);
            // Reset payment method selections
            setPaymentMethodSelected(false);
            setPaymentMethod('card');
            setCashTendered('');
            setCheckNumber('');
          }
        }}>
              <DialogContent className="max-w-4xl top-[10%] translate-y-0">
                <DialogHeader>
                  <DialogTitle>
                    {!paymentMethodSelected && 'Select Payment Method'}
                    {paymentMethodSelected && paymentMethod === 'cash' && 'Cash Payment'}
                    {paymentMethodSelected && paymentMethod === 'card' && 'Card Payment'}
                    {paymentMethodSelected && paymentMethod === 'check' && 'Check Payment'}
                  </DialogTitle>
                </DialogHeader>
                
                {/* Stripe Terminal Reader Info */}
                {!paymentMethodSelected && connectedReader && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-800">
                      <CreditCard className="h-5 w-5" />
                      <span className="font-semibold">Card Reader:</span>
                      <span>{connectedReader.label || connectedReader.serial_number || 'Stripe Reader'}</span>
                      <span className="text-xs text-blue-600">({connectedReader.device_type || 'Unknown'})</span>
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      Reader is configured and ready for card payments
                    </div>
                  </div>
                )}
                
                {/* CC Fee Disclosure Notice */}
                {!paymentMethodSelected && ccFeeEnabled && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-800 text-sm">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>
                        A <strong>{(ccFeeRate * 100).toFixed(1)}%</strong> processing fee 
                        (<strong>${(baseInvoiceTotal * ccFeeRate).toFixed(2)}</strong>) will be added to card payments.
                        Total with fee: <strong>${(baseInvoiceTotal + baseInvoiceTotal * ccFeeRate).toFixed(2)}</strong>
                      </span>
                    </div>
                  </div>
                )}

                {/* Payment Method Selection Buttons - Large Touch-Friendly */}
                {!paymentMethodSelected && (
                  <div className="grid grid-cols-3 gap-6 py-8">
                    <Button
                      onClick={async () => {
                        // Check card payment mode setting
                        const paymentMode = localStorage.getItem('card_payment_mode') || 'reader_first';
                        
                        if (paymentMode === 'manual_entry') {
                          // Manual entry mode: open Stripe card form directly.
                          // GHL invoice is created AFTER Stripe payment succeeds
                          // to ensure amountDue always matches the charged amount.
                          setPaymentDialogOpen(false);
                          setShowPaymentModal(true);
                        } else {
                          // Reader first mode - show Terminal dialog
                          setPaymentDialogOpen(false);
                          setShowTerminalDialog(true);
                          setTerminalConnectionFailed(false);
                          setTerminalStatus('Checking for reader...');
                          
                          // Try to initialize and reconnect to reader
                          setTimeout(async () => {
                            try {
                              setTerminalStatus('Connecting to reader...');
                              const reader = await stripeTerminal.ensureReaderConnected();
                              
                              if (!reader) {
                                // No reader connected - show fallback option
                                setTerminalStatus('No reader connected');
                                setTerminalConnectionFailed(true);
                              } else {
                                // Reader found - proceed with Terminal payment
                                await processTerminalPayment();
                              }
                            } catch (error) {
                              console.error('Terminal init error:', error);
                              setTerminalStatus('Connection failed');
                              setTerminalConnectionFailed(true);
                            }
                          }, 500);
                        }
                      }}
                      disabled={terminalProcessing || processing}
                      variant="outline"
                      className="h-48 flex flex-col items-center justify-center gap-6 text-3xl font-bold p-8 hover:bg-primary/10 border-2"
                    >
                      {terminalProcessing || processing ? (
                        <>
                          <Loader2 className="h-24 w-24 animate-spin" />
                          <div className="text-sm font-normal">{terminalStatus}</div>
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-24 w-24" />
                          <div>Card</div>
                          {connectedReader && (
                            <div className="text-xs font-normal text-gray-600">
                              {connectedReader.label || 'Reader Ready'}
                            </div>
                          )}
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        setPaymentMethod('cash');
                        setPaymentMethodSelected(true);
                      }}
                      variant="outline"
                      className="h-48 flex flex-col items-center justify-center gap-6 text-3xl font-bold p-8 hover:bg-primary/10 border-2"
                    >
                      <DollarSign className="h-24 w-24" />
                      Cash
                    </Button>
                    <Button
                      onClick={() => {
                        setPaymentMethod('check');
                        setPaymentMethodSelected(true);
                      }}
                      variant="outline"
                      className="h-48 flex flex-col items-center justify-center gap-6 text-3xl font-bold p-8 hover:bg-primary/10 border-2"
                    >
                      <Receipt className="h-24 w-24" />
                      Check
                    </Button>
                  </div>
                )}
                
                {/* Cash Payment - Wider Layout */}
                {paymentMethodSelected && paymentMethod === 'cash' && (
                  <div className="grid grid-cols-2 gap-6 py-4">
                    {/* Left Column - Input & Buttons */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="cashTendered" className="text-lg">Amount Tendered</Label>
                        <Input
                          id="cashTendered"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={cashTendered}
                          onChange={(e) => setCashTendered(e.target.value)}
                          className="text-3xl h-16"
                          style={{
                            /* Make number input arrows much bigger for touch */
                            WebkitAppearance: 'textfield',
                          }}
                          autoFocus
                        />
                        {/* Custom large up/down/clear buttons for touch-friendly operation */}
                        <div className="grid grid-cols-3 gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return Math.max(0, current - 0.01).toFixed(2);
                            })}
                            className="h-14 text-xl font-bold"
                          >
                            ▼ Down
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return (current + 0.01).toFixed(2);
                            })}
                            className="h-14 text-xl font-bold"
                          >
                            ▲ Up
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered('0.00')}
                            className="h-14 text-xl font-bold bg-red-50 hover:bg-red-100 text-red-700"
                          >
                            Clear
                          </Button>
                        </div>
                      </div>
                      
                      {/* Quick Tender Buttons - Bills */}
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-gray-700">Quick Add - Bills</div>
                        {/* Row 1: Exact, $1, $5, $10 */}
                        <div className="grid grid-cols-4 gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered(invoiceTotal.toFixed(2))}
                            className="w-full h-14 text-base font-semibold"
                          >
                            Exact
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return (current + 1).toFixed(2);
                            })}
                            className="w-full h-14 text-xl font-bold"
                          >
                            $1
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return (current + 5).toFixed(2);
                            })}
                            className="w-full h-14 text-xl font-bold"
                          >
                            $5
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return (current + 10).toFixed(2);
                            })}
                            className="w-full h-14 text-xl font-bold"
                          >
                            $10
                          </Button>
                        </div>
                        {/* Row 2: $20, $50, $100 */}
                        <div className="grid grid-cols-3 gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return (current + 20).toFixed(2);
                            })}
                            className="w-full h-14 text-xl font-bold"
                          >
                            $20
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return (current + 50).toFixed(2);
                            })}
                            className="w-full h-14 text-xl font-bold"
                          >
                            $50
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return (current + 100).toFixed(2);
                            })}
                            className="w-full h-14 text-xl font-bold"
                          >
                            $100
                          </Button>
                        </div>
                      </div>
                      
                      {/* Quick Tender Buttons - Coins */}
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-gray-700">Quick Add - Coins</div>
                        <div className="grid grid-cols-3 gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return (current + 0.05).toFixed(2);
                            })}
                            className="h-12 text-base font-semibold"
                          >
                            +5¢
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return (current + 0.10).toFixed(2);
                            })}
                            className="h-12 text-base font-semibold"
                          >
                            +10¢
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCashTendered((prev) => {
                              const current = parseFloat(prev) || 0;
                              return (current + 0.25).toFixed(2);
                            })}
                            className="h-12 text-base font-semibold"
                          >
                            +25¢
                          </Button>
                        </div>
                      </div>
                      
                      {/* Open Drawer & Print Receipt Button - or Reprint/Reopen buttons after success */}
                      {!drawerOpenedAndPrinted ? (
                        <Button
                          onClick={openDrawerAndPrint}
                          disabled={processing || printing || changeOwed < 0}
                          className="w-full h-16 text-lg font-semibold"
                        >
                          {printing ? (
                            <>
                              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                              Opening & Printing...
                            </>
                          ) : (
                            <>
                              <Printer className="h-5 w-5 mr-2" />
                              Open Drawer & Print Receipt
                            </>
                          )}
                        </Button>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            onClick={reprintCashCheckReceipt}
                            disabled={printing}
                            variant="outline"
                            className="h-14 text-base font-semibold"
                          >
                            <Printer className="h-4 w-4 mr-2" />
                            Reprint Receipt
                          </Button>
                          <Button
                            onClick={reopenCashDrawer}
                            disabled={printing}
                            variant="outline"
                            className="h-14 text-base font-semibold"
                          >
                            <DollarSign className="h-4 w-4 mr-2" />
                            Reopen Drawer
                          </Button>
                        </div>
                      )}
                      
                      {/* Complete Without Receipt Button - Conditionally Rendered */}
                      {allowCompleteWithoutPrinting && !drawerOpenedAndPrinted && (
                        <Button
                          onClick={async () => {
                            try {
                              const receiptSettingsStr = localStorage.getItem('receipt_settings');
                              const settings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {};
                              const printer = localStorage.getItem('selected_printer') || settings.printerName;
                              if (printer) {
                                unifiedPrintingService.openCashDrawer(printer).catch(e => console.log('Drawer kick failed:', e));
                              }
                            } catch (e) {
                              console.error(e);
                            }
                            completePayment({ skipPrint: true });
                          }}
                          disabled={processing || printing || changeOwed < 0}
                          className="w-full h-12 text-base font-semibold bg-gray-600 hover:bg-gray-700 mb-2"
                        >
                          {processing ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Completing...
                            </>
                          ) : (
                            'Open Drawer & Complete Without Receipt'
                          )}
                        </Button>
                      )}
                      
                      {/* Complete Transaction Button */}
                      <Button
                        onClick={completePayment}
                        disabled={processing || printing || changeOwed < 0 || !drawerOpenedAndPrinted}
                        className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Completing...
                          </>
                        ) : (
                          <>
                            Complete Transaction
                          </>
                        )}
                      </Button>
                      
                      {!drawerOpenedAndPrinted && (
                        <p className="text-sm text-gray-600 text-center">
                          Step 1: Open drawer & print receipt<br/>
                          Step 2: Give change to customer<br/>
                          Step 3: Click Complete when done
                        </p>
                      )}
                    </div>
                    
                    {/* Right Column - Totals & Change */}
                    <div className="bg-gray-50 p-6 rounded-lg space-y-3">
                      <div className="flex justify-between text-lg">
                        <span>Total Due:</span>
                        <span className="font-semibold">${invoiceTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg">
                        <span>Cash Tendered:</span>
                        <span className="font-semibold">${cashAmount.toFixed(2)}</span>
                      </div>
                      <div className="h-px bg-gray-300 my-2" />
                      <div className="flex justify-between text-2xl font-bold">
                        <span>Change:</span>
                        <span className={changeOwed >= 0 ? 'text-green-600' : 'text-red-600'}>
                          ${Math.abs(changeOwed).toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Change Breakdown */}
                      {changeOwed > 0 && (() => {
                        // Calculate breakdown inline to avoid memoization issues
                        const breakdown = [];
                        let remaining = Math.round(changeOwed * 100);
                        const denominations = [
                          { value: 10000, name: '$100 bill', plural: '$100 bills', amount: 100 },
                          { value: 5000, name: '$50 bill', plural: '$50 bills', amount: 50 },
                          { value: 2000, name: '$20 bill', plural: '$20 bills', amount: 20 },
                          { value: 1000, name: '$10 bill', plural: '$10 bills', amount: 10 },
                          { value: 500, name: '$5 bill', plural: '$5 bills', amount: 5 },
                          { value: 100, name: '$1 bill', plural: '$1 bills', amount: 1 },
                          { value: 25, name: 'quarter', plural: 'quarters', amount: 0.25 },
                          { value: 10, name: 'dime', plural: 'dimes', amount: 0.10 },
                          { value: 5, name: 'nickel', plural: 'nickels', amount: 0.05 },
                          { value: 1, name: 'penny', plural: 'pennies', amount: 0.01 }
                        ];
                        
                        for (const denom of denominations) {
                          if (remaining >= denom.value) {
                            const count = Math.floor(remaining / denom.value);
                            breakdown.push({
                              count,
                              name: count === 1 ? denom.name : denom.plural,
                              amount: denom.amount
                            });
                            remaining -= count * denom.value;
                          }
                        }
                        
                        return (
                          <div className="mt-3 pt-3 border-t border-gray-300">
                            <div className="text-sm font-semibold text-gray-700 mb-2">Change Breakdown:</div>
                            <div className="space-y-1">
                              {breakdown.map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm text-gray-600">
                                  <span>{item.count} × {item.name}</span>
                                  <span className="text-gray-800 font-medium">
                                    ${(item.count * item.amount).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Check Payment - Horizontal Layout */}
                {paymentMethod === 'check' && (
                  <div className="grid grid-cols-2 gap-6 py-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="checkNumber" className="text-lg">Check Number</Label>
                        <Input
                          id="checkNumber"
                          type="text"
                          placeholder="Enter check number"
                          value={checkNumber}
                          onChange={(e) => setCheckNumber(e.target.value)}
                          className="text-3xl h-16"
                          autoFocus
                        />
                      </div>
                      
                      {/* Open Drawer & Print Receipt Button for Check Payment - or Reprint/Reopen buttons after success */}
                      {!drawerOpenedAndPrinted ? (
                        <Button
                          onClick={openDrawerAndPrint}
                          disabled={processing || printing || !checkNumber}
                          className="w-full h-16 text-lg font-semibold"
                        >
                          {printing ? (
                            <>
                              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                              Opening & Printing...
                            </>
                          ) : (
                            <>
                              <Printer className="h-5 w-5 mr-2" />
                              Open Drawer & Print Receipt
                            </>
                          )}
                        </Button>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <Button
                            onClick={reprintCashCheckReceipt}
                            disabled={printing}
                            variant="outline"
                            className="h-14 text-base font-semibold"
                          >
                            <Printer className="h-4 w-4 mr-2" />
                            Reprint Receipt
                          </Button>
                          <Button
                            onClick={reopenCashDrawer}
                            disabled={printing}
                            variant="outline"
                            className="h-14 text-base font-semibold"
                          >
                            <DollarSign className="h-4 w-4 mr-2" />
                            Reopen Drawer
                          </Button>
                        </div>
                      )}
                      
                      {/* Complete Without Receipt Button - Conditionally Rendered */}
                      {allowCompleteWithoutPrinting && !drawerOpenedAndPrinted && (
                        <Button
                          onClick={async () => {
                            try {
                              const receiptSettingsStr = localStorage.getItem('receipt_settings');
                              const settings = receiptSettingsStr ? JSON.parse(receiptSettingsStr) : {};
                              const printer = localStorage.getItem('selected_printer') || settings.printerName;
                              if (printer) {
                                unifiedPrintingService.openCashDrawer(printer).catch(e => console.log('Drawer kick failed:', e));
                              }
                            } catch (e) {
                              console.error(e);
                            }
                            completePayment({ skipPrint: true });
                          }}
                          disabled={processing || printing || !checkNumber}
                          className="w-full h-12 text-base font-semibold bg-gray-600 hover:bg-gray-700 mb-2"
                        >
                          {processing ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Completing...
                            </>
                          ) : (
                            'Open Drawer & Complete Without Receipt'
                          )}
                        </Button>
                      )}
                      
                      {/* Complete Transaction Button */}
                      <Button
                        onClick={completePayment}
                        disabled={processing || printing || !checkNumber || !drawerOpenedAndPrinted}
                        className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Completing...
                          </>
                        ) : (
                          <>
                            Complete Transaction
                          </>
                        )}
                      </Button>
                      
                      {!drawerOpenedAndPrinted && (
                        <p className="text-sm text-gray-600 text-center">
                          Step 1: Open drawer & print receipt<br/>
                          Step 2: Give check to customer<br/>
                          Step 3: Click Complete when done
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-gray-50 p-6 rounded-lg flex items-center justify-center">
                      <div className="text-center space-y-2">
                        <div className="text-lg text-gray-600">Total Amount</div>
                        <div className="text-5xl font-bold">${invoiceTotal.toFixed(2)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>

      {/* Activate Rewards Dialog */}
      <Dialog open={activateRewardsDialogOpen} onOpenChange={setActivateRewardsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Contact & Activate Rewards</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Name Fields - 2 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="rewards-firstName">First Name *</Label>
                <Input
                  id="rewards-firstName"
                  placeholder="John"
                  value={rewardsForm.firstName}
                  onChange={(e) => setRewardsForm({ ...rewardsForm, firstName: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rewards-lastName">Last Name</Label>
                <Input
                  id="rewards-lastName"
                  placeholder="Doe"
                  value={rewardsForm.lastName}
                  onChange={(e) => setRewardsForm({ ...rewardsForm, lastName: e.target.value })}
                />
              </div>
            </div>

            {/* Contact Fields - 2 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="rewards-phone">Phone *</Label>
                <Input
                  id="rewards-phone"
                  placeholder="+1 (555) 123-4567"
                  value={rewardsForm.phone}
                  onChange={(e) => setRewardsForm({ ...rewardsForm, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rewards-email">Email</Label>
                <Input
                  id="rewards-email"
                  type="email"
                  placeholder="john@example.com"
                  value={rewardsForm.email}
                  onChange={(e) => setRewardsForm({ ...rewardsForm, email: e.target.value })}
                />
              </div>
            </div>
            
            {/* Address Section */}
            <div className="pt-2 border-t">
              <Label className="text-sm font-semibold mb-3 block">Address (Optional)</Label>
              <div className="space-y-3">
                <Input
                  id="rewards-address1"
                  placeholder="Street Address"
                  value={rewardsForm.address1}
                  onChange={(e) => setRewardsForm({ ...rewardsForm, address1: e.target.value })}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    id="rewards-city"
                    placeholder="City"
                    value={rewardsForm.city}
                    onChange={(e) => setRewardsForm({ ...rewardsForm, city: e.target.value })}
                  />
                  <Input
                    id="rewards-state"
                    placeholder="State"
                    value={rewardsForm.state}
                    onChange={(e) => setRewardsForm({ ...rewardsForm, state: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    id="rewards-postalCode"
                    placeholder="Postal Code"
                    value={rewardsForm.postalCode}
                    onChange={(e) => setRewardsForm({ ...rewardsForm, postalCode: e.target.value })}
                  />
                  <Input
                    id="rewards-country"
                    placeholder="Country"
                    value={rewardsForm.country}
                    onChange={(e) => setRewardsForm({ ...rewardsForm, country: e.target.value })}
                  />
                </div>
              </div>
            </div>
            
            <Button 
              className="w-full" 
              size="lg"
              onClick={saveAndActivateRewards}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Save & Activate Rewards
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Overlay */}
      {showSuccessOverlay && (
        <div className="fixed inset-0 bg-green-600 bg-opacity-95 z-[9999] flex items-center justify-center animate-in fade-in duration-300">
          <div className="text-center text-white">
            <CheckCircle className="h-32 w-32 mx-auto mb-6 animate-in zoom-in duration-500" />
            <h2 className="text-4xl font-bold mb-2">Payment Complete!</h2>
            <p className="text-xl">Returning to dashboard...</p>
          </div>
        </div>
      )}

      {/* Stripe Payment Modal */}
      {/* Terminal Payment Dialog */}
      <Dialog open={showTerminalDialog} onOpenChange={async (open) => {
        if (!open) {
          // User is closing the dialog — cancel any in-progress payment
          if (terminalProcessing) {
            try {
              await stripeTerminal.cancelPayment();
            } catch (e) {
              console.error('Cancel on close error:', e);
            }
            setTerminalProcessing(false);
          }
          setShowTerminalDialog(false);
          setTerminalConnectionFailed(false);
          setTerminalStatus('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Card Payment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {terminalConnectionFailed ? (
              <>
                <div className="text-center py-6">
                  <div className="text-red-600 mb-2">
                    <CreditCard className="h-16 w-16 mx-auto opacity-50" />
                  </div>
                  <div className="text-lg font-semibold text-red-900 mb-2">{terminalStatus}</div>
                  <div className="text-sm text-gray-600">
                    The card reader could not be reached. You can use manual entry instead.
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowTerminalDialog(false);
                      setTerminalConnectionFailed(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      setShowTerminalDialog(false);
                      setTerminalConnectionFailed(false);
                      
                      // Switch to manual entry — Stripe first, GHL invoice after
                      setShowPaymentModal(true);
                    }}
                  >
                    Use Manual Entry
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center py-6">
                  <Loader2 className="h-16 w-16 mx-auto animate-spin text-blue-600 mb-4" />
                  <div className="text-lg font-semibold mb-2">{terminalStatus}</div>
                  {(terminalStatus.includes('swipe') || terminalStatus.includes('Insert')) && (
                    <div className="text-sm text-gray-600">
                      Follow the prompts on your card reader
                    </div>
                  )}
                </div>
                
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      await stripeTerminal.cancelPayment();
                    } catch (error) {
                      console.error('Cancel error:', error);
                    }
                    setTerminalProcessing(false);
                    setShowTerminalDialog(false);
                    setTerminalStatus('');
                  }}
                  className="w-full h-12 text-base font-semibold border-red-200 text-red-700 hover:bg-red-50"
                >
                  Cancel Transaction
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Print Receipt Prompt Dialog */}
      <Dialog open={printPromptOpen} onOpenChange={setPrintPromptOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <CheckCircle className="text-green-500 w-6 h-6" />
              Payment Successful!
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-6 py-4">
            <p className="text-center text-gray-600 font-medium text-lg">
              Would you like to print a receipt for this transaction?
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Button 
                variant="outline" 
                size="lg"
                className="h-14 text-base font-semibold border-gray-300"
                onClick={() => {
                  setPrintPromptOpen(false);
                  finishPaymentFlow();
                }}
              >
                No Thanks
              </Button>
              <Button 
                size="lg"
                className="h-14 text-base font-semibold bg-blue-600 hover:bg-blue-700"
                onClick={async () => {
                  setPrintPromptOpen(false);
                  if (pendingReceiptData) {
                    await printReceipt(pendingReceiptData);
                  }
                  finishPaymentFlow();
                }}
              >
                <Printer className="w-5 h-5 mr-2" />
                Print Receipt
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={handlePaymentModalClose}
        amount={invoiceTotal}
        invoiceId={pendingInvoiceId || (invoiceId !== 'new' ? invoiceId : null)}
      />
    </div>
    </>
  );
}
